import React, { useEffect, useState, useMemo } from "react";
import {
    Alert, Button, Card, CardBody, Col, Container, FormGroup, Label,
    Modal, ModalBody, ModalHeader, Row, Spinner, Table
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import Session from "../../auth/Session";
import { Roles } from "../../auth/Roles";
import useSortableData from "../useSortableData";
import SortableHeader from "../SortableHeader";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "");
const money = (n) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ApprovedInvoicesReport = () => {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [voiding, setVoiding] = useState(false);
    const [pushingId, setPushingId] = useState(null);
    const [posting, setPosting] = useState(false);
    const [error, setError] = useState("");
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [qboTarget, setQboTarget] = useState(null);
    const [qboPreview, setQboPreview] = useState(null);
    const [qboPreviewOpen, setQboPreviewOpen] = useState(false);
    const [qboBlocked, setQboBlocked] = useState(false);

    const sortGetters = useMemo(() => ({
        week: (i) => (i.weekStartDate ? new Date(i.weekStartDate) : null),
        invoiceDate: (i) => (i.invoiceDate ? new Date(i.invoiceDate) : null),
        invoiceAmount: (i) => Number(i.invoiceAmount ?? 0),
        approvalDate: (i) => (i.approvalDate ? new Date(i.approvalDate) : null),
        voidedDate: (i) => (i.voidedDate ? new Date(i.voidedDate) : null),
    }), []);

    const { items: sortedInvoices, sortConfig, requestSort } = useSortableData(
        invoices,
        { key: "week", direction: "asc", getters: sortGetters }
    );

    const loadInvoices = async () => {
        if (!Session.hasRole(Roles.Admin)) {
            navigate("/accessdenied");
            return;
        }

        setLoading(true);
        setError("");
        try {
            const token = await Session.getToken();
            const res = await fetch("/api/reports/invoices", {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            setInvoices(await res.json());
        } catch (e) {
            setError(e.message || "Failed to load invoice report.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadInvoices();
    }, []);

    const voidInvoice = async () => {
        if (!selectedInvoice) return;

        setVoiding(true);
        setError("");
        try {
            const token = await Session.getToken();
            const res = await fetch(`/api/reports/invoices/${selectedInvoice.invoiceId}/void`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            setInvoices(await res.json());
            setSelectedInvoice(null);
        } catch (e) {
            setError(e.message || "Failed to void invoice.");
        } finally {
            setVoiding(false);
        }
    };

    // Step 1: run the same validation the audit page uses (line items resolve to QuickBooks
    // items). If it passes, show a confirmation that the data will be posted; if not, show the
    // blocked preview so the user can fix the unlinked items.
    const startPush = async (invoice) => {
        setPushingId(invoice.invoiceId);
        setError("");
        try {
            const token = await Session.getToken();
            const res = await fetch(`/api/reports/invoices/${invoice.invoiceId}/quickbooks-preview`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`${await res.text()}`);
            const preview = await res.json();
            setQboTarget(invoice);
            setQboPreview(preview);
            setQboBlocked(!preview?.isValid);
            setQboPreviewOpen(true);
        } catch (e) {
            setError(e.message || "Failed to validate invoice for QuickBooks.");
        } finally {
            setPushingId(null);
        }
    };

    // Step 2: validation passed and the user confirmed — actually post to QuickBooks.
    const confirmPush = async () => {
        if (!qboTarget) return;
        setPosting(true);
        setError("");
        try {
            const token = await Session.getToken();
            const res = await fetch(`/api/reports/invoices/${qboTarget.invoiceId}/push-quickbooks`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`${await res.text()}`);
            setInvoices(await res.json());
            setQboPreviewOpen(false);
            setQboTarget(null);
        } catch (e) {
            setError(e.message || "Failed to push invoice to QuickBooks.");
        } finally {
            setPosting(false);
        }
    };

    return (
        <Container className="mt-0">
            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col><h5>Approved Invoice Report</h5></Col>
                    </Row>

                    {error && <Alert color="danger">{error}</Alert>}

                    {loading ? (
                        <div className="text-center py-4"><Spinner /></div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Week" sortKey="week" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Customer" sortKey="customerName" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Sub-Customer" sortKey="subCustomerName" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Invoice Date" sortKey="invoiceDate" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Invoice Total" sortKey="invoiceAmount" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <SortableHeader label="Approved By" sortKey="approvedBy" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Approved Date" sortKey="approvalDate" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Voided By" sortKey="voidedBy" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Voided Date" sortKey="voidedDate" sortConfig={sortConfig} onSort={requestSort} />
                                    <th>QuickBooks</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedInvoices.length === 0 ? (
                                    <tr><td colSpan="12" className="text-center">No approved invoices found.</td></tr>
                                ) : sortedInvoices.map(invoice => (
                                    <tr key={invoice.invoiceId} className={invoice.voided ? "table-warning" : ""}>
                                        <td>{invoice.status}</td>
                                        <td>{fmtDate(invoice.weekStartDate)} - {fmtDate(invoice.weekEndDate)}</td>
                                        <td>{invoice.customerName}</td>
                                        <td>{invoice.subCustomerName}</td>
                                        <td>{fmtDate(invoice.invoiceDate)}</td>
                                        <td style={{ textAlign: "right" }}>${money(invoice.invoiceAmount)}</td>
                                        <td>{invoice.approvedBy}</td>
                                        <td>{fmt(invoice.approvalDate)}</td>
                                        <td>{invoice.voidedBy}</td>
                                        <td>{fmt(invoice.voidedDate)}</td>
                                        <td className="text-center">
                                            {invoice.voided ? (
                                                <span className="text-muted">—</span>
                                            ) : invoice.qbInvoiceId ? (
                                                <span className="text-success">Synced {fmt(invoice.qbSyncedAt)}</span>
                                            ) : (
                                                <>
                                                    <Button size="sm" color="primary" disabled={pushingId === invoice.invoiceId} onClick={() => startPush(invoice)}>
                                                        {pushingId === invoice.invoiceId ? <><Spinner size="sm" /> Checking...</> : "Send to QuickBooks"}
                                                    </Button>
                                                    {invoice.qbSyncError && <div className="text-danger small mt-1">{invoice.qbSyncError}</div>}
                                                </>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <Button size="sm" color="secondary" outline className="me-2" onClick={() => navigate(`/reports/invoice-view/${invoice.invoiceId}`)}>
                                                View
                                            </Button>
                                            {!invoice.voided && (
                                                <Button size="sm" color="warning" onClick={() => setSelectedInvoice(invoice)}>
                                                    Void
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={qboPreviewOpen} toggle={() => !posting && setQboPreviewOpen(false)} size="lg">
                <ModalHeader toggle={() => !posting && setQboPreviewOpen(false)}>
                    {qboBlocked ? "Cannot Push to QuickBooks" : "Confirm QuickBooks Push"}
                    {qboPreview?.docNumber ? ` — #${qboPreview.docNumber}` : ""}
                </ModalHeader>
                <ModalBody>
                    {qboBlocked ? (
                        <Alert color="danger" className="mb-3">
                            This invoice <strong>cannot be pushed</strong> because one or more line items are not
                            linked to a QuickBooks item. Link the highlighted items, then try again.
                        </Alert>
                    ) : (
                        <Alert color="info" className="mb-3">
                            Validation passed. This invoice <strong>will be posted to QuickBooks</strong> when you
                            confirm below.
                        </Alert>
                    )}
                    {qboPreview && (
                        <>
                            <Row className="mb-3">
                                <Col md={6}><div className="text-muted small">Customer</div><div>{qboPreview.customerName || "—"}</div></Col>
                                <Col md={3}><div className="text-muted small">Txn Date</div><div>{qboPreview.txnDate || "—"}</div></Col>
                                <Col md={3}><div className="text-muted small">Service Date</div><div>{qboPreview.serviceDate || "—"}</div></Col>
                            </Row>
                            {qboPreview.warnings && qboPreview.warnings.length > 0 && (
                                <Alert color="warning">
                                    <div className="fw-bold mb-1">Issues that would affect this push:</div>
                                    <ul className="mb-0">
                                        {qboPreview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                    </ul>
                                </Alert>
                            )}
                            <Table bordered size="sm" responsive>
                                <thead className="table-light">
                                    <tr>
                                        <th>Description</th>
                                        <th>QB Item</th>
                                        <th className="text-center">Qty</th>
                                        <th className="text-end">Unit Price</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(qboPreview.lines || []).map((l, i) => (
                                        <tr key={i}>
                                            <td>{l.description}</td>
                                            <td>{l.itemName || <span className="text-danger">— not linked —</span>}</td>
                                            <td className="text-center">{Number(l.qty ?? 0)}</td>
                                            <td className="text-end">${money(l.unitPrice)}</td>
                                            <td className="text-end">${money(l.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="4" className="text-end fw-bold">Total</td>
                                        <td className="text-end fw-bold">${money(qboPreview.total)}</td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </>
                    )}
                    <div className="d-flex justify-content-end gap-2">
                        {qboBlocked ? (
                            <Button color="primary" onClick={() => setQboPreviewOpen(false)}>Close</Button>
                        ) : (
                            <>
                                <Button color="secondary" onClick={() => setQboPreviewOpen(false)} disabled={posting}>Cancel</Button>
                                <Button color="primary" onClick={confirmPush} disabled={posting}>
                                    {posting ? <><Spinner size="sm" /> Posting...</> : "Post to QuickBooks"}
                                </Button>
                            </>
                        )}
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={!!selectedInvoice} toggle={() => setSelectedInvoice(null)}>
                <ModalHeader toggle={() => setSelectedInvoice(null)}>Void Invoice</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>
                            This will void the selected invoice and any paysheets associated with that invoice. The records will remain available in reports, and finance can regenerate new versions.
                        </Label>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setSelectedInvoice(null)} disabled={voiding}>Cancel</Button>
                        <Button color="warning" onClick={voidInvoice} disabled={voiding}>
                            {voiding ? <><Spinner size="sm" /> Voiding...</> : "Void Invoice"}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default ApprovedInvoicesReport;
